import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ShieldCheck } from 'lucide-react';
import { setAuthToken } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Landing page for the Keycloak callback.
 *
 * The backend redirects here with the session JWT in the URL *fragment*
 * (#token=...). Fragments are never sent to the server, so the token does not
 * end up in nginx or Cloudflare access logs. We move it into localStorage,
 * clear it from the address bar, and let AuthContext re-check the session.
 */
export const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [message, setMessage] = useState('Completing sign-in...');

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const token = params.get('token');

    if (!token) {
      setMessage('No sign-in token received. Redirecting to login...');
      window.setTimeout(() => navigate('/login', { replace: true }), 1500);
      return;
    }

    setAuthToken(token);
    // Strip the token from the address bar/history before doing anything else.
    window.history.replaceState(null, '', window.location.pathname);

    checkAuth()
      .then(() => navigate('/dashboard', { replace: true }))
      .catch(() => {
        setMessage('Sign-in could not be completed. Redirecting to login...');
        window.setTimeout(() => navigate('/login', { replace: true }), 1500);
      });
  }, [checkAuth, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200">
      <div className="text-center">
        <ShieldCheck className="w-10 h-10 mx-auto mb-4 text-primary" />
        <p>{message}</p>
        <span className="loading loading-spinner loading-md mt-4 text-primary"></span>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
